import { useEffect, useState } from 'react';
import { faro, LogLevel } from '@grafana/faro-react';
import { useSelector } from 'react-redux';
import { provisionUser } from '../lib/api/userService/userService';
import { oktaDS3Iss } from '../lib/environment-config';
import { useDispatch } from '../lib/reduxHooks';
import type { IRootState } from '../store';
import * as actions from '../store/pageFrame/actions';
import { useAuth, useUserClaims } from './use-auth-compat';

export type User = {
  userId: string;
  oktaUserId: string;
  email: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  oktaGroups: string[];
  subscriberId?: number;
  primeUserId?: string;
  primeUserGuid?: string;
  primeContactType?: string;
};

export type UserInfoApi = {
  user: User;
  impersonating?: User;
};

export type UserInfo = {
  provisioned: boolean;
  digisignAccountId: string;
  oktaUserId: string;
  senderId: string;
  senderFirstName: string;
  senderLastName: string;
  senderEmail: string;
  subscriberId: string;
  primeContactType: string;
  digisign_fnf_account_id?: string;
  isPrimeAuthenticated: boolean;
  impersonatedPrimeUserGuid?: string;
  // True under support impersonation, which is the only thing that sets
  // delegated_user_id. Then every other field here describes the person being
  // acted for, so nothing else tells that caller apart from the owner. A Forms
  // permission delegate carries no such claim and reads as false here.
  isDelegate: boolean;
};
// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const selector = (state: IRootState) => ({
  isFetchingUserInfo: state.pageFrame.isFetchingUserInfo,
  isUserInfoFetched: state.pageFrame.isUserInfoFetched,
});

interface IState {
  isFetchingUserInfo: boolean;
  isUserInfoFetched: boolean;
}

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default function useUserInfo() {
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  const { isFetchingUserInfo, isUserInfoFetched }: IState = useSelector(selector);
  const dispatch = useDispatch();
  const { userManager, authState } = useAuth();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const { claims } = authState.accessToken ?? {};
  const { claims: userClaims } = useUserClaims();

  const [digisignAccountId, setDigisignAccountId] = useState<string | null>(null);
  const [needsProvisioning, setNeedsProvisioning] = useState<boolean>(true);
  const [delegatedUserOktaId, setDelegatedUserOktaId] = useState<string | null | undefined>(null);
  const [needsToSetUserInfo, setNeedsToSetUserInfo] = useState<boolean>(false);

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const setImpersonatedUserInfo = (userInfoTemp: UserInfoApi) => {
    setUserInfo({
      isDelegate: true,
      provisioned: !needsProvisioning,
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      digisignAccountId,
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      oktaUserId: delegatedUserOktaId,
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      senderId: userInfoTemp.impersonating?.userId,
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      senderFirstName: userInfoTemp.impersonating?.firstName,
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      senderLastName: userInfoTemp.impersonating?.lastName,
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      senderEmail: userInfoTemp.impersonating?.email,
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      subscriberId: userInfoTemp.impersonating?.subscriberId,
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      primeContactType: userInfoTemp.impersonating?.primeContactType,
      digisign_fnf_account_id: claims?.digisign_fnf_account_id,
      isPrimeAuthenticated: !!userInfoTemp.impersonating?.oktaGroups?.find((g: string) => g === '/prime/users'),
      impersonatedPrimeUserGuid: userInfoTemp.impersonating?.primeUserGuid,
    });
  };

  useEffect(() => {
    if (!isUserInfoFetched) {
      return;
    }
    setNeedsToSetUserInfo(false);
    const userInfoString = sessionStorage.getItem('userInfo');
    const storedUserInfo = userInfoString ? JSON.parse(userInfoString) : null;
    setImpersonatedUserInfo(storedUserInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [isUserInfoFetched]);

  useEffect(() => {
    if (!needsToSetUserInfo) {
      return;
    }
    if (!delegatedUserOktaId) {
      setUserInfo({
        isDelegate: false,
        provisioned: !needsProvisioning,
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        digisignAccountId,
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        oktaUserId: claims.OktaUserId || claims.uid,
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        senderId: claims.UserId || claims.user_id || claims.sub,
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        senderFirstName: claims.first_name || claims.FirstName || claims.given_name,
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        senderLastName: claims.last_name || claims.LastName || claims.family_name,
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        senderEmail: claims.email || claims.SenderEmail || claims.Email || claims.sub,
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        subscriberId: claims.subscriber_id + '',
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        primeContactType: userClaims?.prime_contact_type || claims.prime_contact_type,
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        digisign_fnf_account_id: claims.digisign_fnf_account_id,
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        isPrimeAuthenticated: !!claims.groups?.find((g: string) => g === '/prime/users'),
      });
      setNeedsToSetUserInfo(false);
    } else if (isUserInfoFetched) {
      const userInfoString = sessionStorage.getItem('userInfo');
      const storedUserInfo = userInfoString ? JSON.parse(userInfoString) : null;
      setImpersonatedUserInfo(storedUserInfo);
      setNeedsToSetUserInfo(false);
    } else if (!isFetchingUserInfo) {
      dispatch(actions.updateisFetchingUserInfo(true));
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      dispatch(actions.fetchUserInfo(digisignAccountId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [needsToSetUserInfo]);

  useEffect(() => {
    if (!claims || !userManager || (authState.idToken && !userClaims)) {
      setUserInfo(null);
      return;
    }
    const digisignAccountIdTemp =
      claims.AccountId ||
      claims.account_id ||
      claims.digisign_prime_account_id ||
      claims.digisign_forms_account_id ||
      claims.digisign_fnf_account_id;
    setDigisignAccountId(digisignAccountIdTemp);
    const delegatedUserOktaIdTemp = claims.delegated_user_id;

    if (delegatedUserOktaIdTemp) {
      setDelegatedUserOktaId(delegatedUserOktaIdTemp);
    }

    const isDS3OktaToken = claims.iss === oktaDS3Iss;
    // For impersonated users this will never provision because the user_id is the impersonated user's id
    // Now, we check for this during the fetchUserInfo as well because we'll know at that point if the impersonated
    // user exists in the user service
    const needsProvisioningTemp =
      !(claims.UserId || claims.user_id) ||
      (isDS3OktaToken && !claims.groups?.find((g: string) => g.includes(`/digisign/${digisignAccountIdTemp}`)));

    setNeedsProvisioning(needsProvisioningTemp);
    if (needsProvisioningTemp) {
      provisionUser(digisignAccountIdTemp).then(({ userId }) => {
        userManager._oktaAuth.oktaAuth.tokenManager.renew('accessToken');

        faro?.api?.pushLog(['User Provisioned'], {
          level: LogLevel.LOG,
          context: { userId },
        });
      });
    }

    setNeedsToSetUserInfo(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [claims, userManager, userClaims]);

  return userInfo;
}
